import { establishSession } from '@/platform/auth/establishSession';
import { persistCreateOwnerEmail } from '@/platform/saas/createOwnerEmailCookie';
import { bounceIfOpsAuthReturn, persistOpsAuthSession } from '@/platform/lib/idTokenStore';
import { getFirebaseAuth } from '@/platform/lib/firebase';
import { hostAwareReturnTo } from '@/platform/saas/hostPostLogin';

export type FinishApexHostLoginDeps = {
  establishSession: () => Promise<unknown>;
  persistCreateOwnerEmail: (email: string) => void;
  persistOpsAuthSession: (idToken: string) => void;
  getIdToken: () => Promise<string | undefined>;
  getEmail: () => string | undefined;
  hostAwareReturnTo: (returnTo: string) => Promise<string>;
  bounceIfOpsAuthReturn?: (token?: string) => Promise<boolean>;
};

export type FinishApexHostLoginResult = {
  dest: string;
  bounced: boolean;
};

/** Bounce + membership dest only (e.g. after TermsRequiredError when cookies already set). */
export async function resolveApexHostLoginDest(
  returnTo: string,
  deps: Pick<FinishApexHostLoginDeps, 'getIdToken' | 'hostAwareReturnTo' | 'bounceIfOpsAuthReturn'>,
): Promise<FinishApexHostLoginResult> {
  const fallback = returnTo.startsWith('/') ? returnTo : '/';
  const token = await deps.getIdToken();
  if (token && deps.bounceIfOpsAuthReturn && (await deps.bounceIfOpsAuthReturn(token))) {
    return { dest: fallback, bounced: true };
  }
  const dest = await deps.hostAwareReturnTo(fallback);
  return { dest, bounced: false };
}

/**
 * Same post-sign-in session for overlay LoginForm and marketing chrome:
 * owner-email cookie + ops token + session cookie, then membership dest.
 */
export async function finishApexHostLogin(
  returnTo: string,
  deps: FinishApexHostLoginDeps,
): Promise<FinishApexHostLoginResult> {
  const email = (deps.getEmail() || '').trim();
  if (email.includes('@')) {
    deps.persistCreateOwnerEmail(email);
  }
  const token = await deps.getIdToken();
  if (token) {
    deps.persistOpsAuthSession(token);
  }
  await deps.establishSession();
  return resolveApexHostLoginDest(returnTo, deps);
}

/** Browser default deps — overlay and marketing chrome share this path. */
export function defaultFinishApexHostLoginDeps(): FinishApexHostLoginDeps {
  return {
    establishSession,
    persistCreateOwnerEmail,
    persistOpsAuthSession,
    async getIdToken() {
      return (await getFirebaseAuth().currentUser?.getIdToken()) || undefined;
    },
    getEmail() {
      return getFirebaseAuth().currentUser?.email || undefined;
    },
    hostAwareReturnTo,
    bounceIfOpsAuthReturn,
  };
}

export async function finishApexHostLoginWithDefaults(
  returnTo: string,
): Promise<FinishApexHostLoginResult> {
  return finishApexHostLogin(returnTo, defaultFinishApexHostLoginDeps());
}

export async function resolveApexHostLoginDestWithDefaults(
  returnTo: string,
): Promise<FinishApexHostLoginResult> {
  return resolveApexHostLoginDest(returnTo, defaultFinishApexHostLoginDeps());
}
